import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button, type ButtonVariant } from "./Button";
import { cn } from "./cn";

export interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  /** Starts a new group with a heading above it. */
  section?: string;
}

export interface MenuButtonProps {
  label: string;
  items: MenuItem[];
  icon?: ReactNode;
  variant?: ButtonVariant;
  disabled?: boolean;
}

/**
 * A button that opens a list of actions — the `GridMenuBar` submenu equivalent.
 *
 * Exists because some screens have more actions than a toolbar can hold: the
 * product page alone has a dozen retailer export formats, which would otherwise
 * be a dozen buttons competing with the ones people use daily.
 */
export function MenuButton({ label, items, icon, variant = "secondary", disabled }: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ right: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  // Against the viewport, in a portal, as MultiSelect does: an absolutely
  // positioned list is clipped by whatever scrolling box the button sits in (a
  // table that scrolls sideways, a dialog body).
  const place = useCallback(() => {
    const rect = container.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 4;
    const margin = 8;
    const below = window.innerHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    const up = below < Math.min(MAX_MENU_HEIGHT, above) && above > below;
    setAnchor({
      right: window.innerWidth - rect.right,
      ...(up ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      maxHeight: Math.max(120, Math.min(MAX_MENU_HEIGHT, up ? above : below)),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Close on outside click and on Escape — a menu that traps the page is worse
  // than no menu.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The list lives in a portal, so it is not inside `container`.
      if (!container.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    // Capture, so scrolling any ancestor moves the list with its button.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  let lastSection: string | undefined;

  return (
    <div ref={container} className="relative">
      <Button
        size="sm"
        variant={variant}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        {...(icon ? { icon } : {})}
      >
        {label}
        <ChevronDown className="size-3.5 opacity-60" />
      </Button>

      {open && anchor && createPortal(
        <div
          ref={panel}
          role="menu"
          style={{
            position: "fixed",
            right: anchor.right,
            ...(anchor.top !== undefined ? { top: anchor.top } : { bottom: anchor.bottom }),
            maxHeight: anchor.maxHeight,
          }}
          className="z-50 w-60 overflow-auto rounded-md border border-line bg-surface-over p-1 shadow-overlay"
        >
          {items.map((item) => {
            const heading = item.section && item.section !== lastSection ? item.section : null;
            lastSection = item.section;

            return (
              <div key={item.label}>
                {heading && (
                  <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                    {heading}
                  </p>
                )}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-ink",
                    "hover:bg-surface-sunk disabled:pointer-events-none disabled:opacity-40",
                  )}
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </button>
              </div>
            );
          })}
        </div>,
        // Inside a dialog it has to be the dialog: showModal() puts it on the
        // top layer, so a list in the body would render behind it.
        container.current?.closest("dialog") ?? document.body,
      )}
    </div>
  );
}

/** The tallest the list grows before it scrolls (max-h-96). */
const MAX_MENU_HEIGHT = 384;
